import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Flex, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import { CompanySettings } from 'common/hooks/useCompanySettings';
import Button from 'components/_core/Button';
import { ERROR_MESSAGE_CLASSNAME } from 'components/_core/form';
import Text from 'components/_core/Text';
import NavigationButton from 'components/NavigationButton';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import {
  getQuoteFormSchema,
  QuoteFormSchema,
} from 'features/Work/form/quoteForm.schema';
import { ConfirmType } from 'features/Work/types';

import FormPageLayout from '../../../../components/FormPageLayout';
import FullPageLayout from '../../../../components/FullPageLayout';
import { useInvoicingSpecialMentionsQuery } from '../../../hooks/useInvoicingSpecialMentionsQuery';
import { QuoteFormContextProvider } from './context';
import { useCachedQuote } from './hooks/useCachedQuote';
import { useQuoteFormQuery } from './hooks/useQuoteFormQuery';
import locales from './locales';
import QuoteForm from './QuoteForm';
import QuotePreview from './QuotePreview';
import * as S from './styles';

interface FormLayoutProps {
  companySettings: CompanySettings;
  quote: QuoteFormSchema;
  isEditMode?: boolean;
  onDelete?: () => void;
  onClose: () => void;
  onConfirm: (quote: QuoteFormSchema, type: ConfirmType) => Promise<void>;
}

const FormLayout: FC<FormLayoutProps> = ({
  companySettings,
  isEditMode = false,
  onClose,
  onConfirm,
  onDelete,
  quote: baseQuote,
}) => {
  const { setCachedQuote } = useCachedQuote();
  const { showToast } = useToast();

  const [confirmType, setConfirmType] = useState<ConfirmType | null>(null);

  const {
    clients,
    company,
    loading: quoteFormLoading,
    metadata,
  } = useQuoteFormQuery();

  const { invoicingSpecialMentions, loading: specialMentionsLoading } =
    useInvoicingSpecialMentionsQuery();

  const formMethods = useForm<QuoteFormSchema>({
    defaultValues: baseQuote,
    mode: 'onChange',
    resolver: zodResolver(
      getQuoteFormSchema({ minLatePaymentFee: metadata.minLatePaymentFee }),
    ),
    shouldFocusError: false,
  });

  const { watch } = formMethods;

  useEffect(() => {
    const subscription = watch((values) => {
      if (!isEditMode) {
        setCachedQuote(values);
      }
    });

    return () => subscription.unsubscribe();
  }, [isEditMode, setCachedQuote, watch]);

  const handleConfirm = (type: ConfirmType) =>
    formMethods.handleSubmit(
      async (values) => {
        setConfirmType(type);
        await onConfirm(values, type);
        setConfirmType(null);
      },
      () => {
        /**
         * manually scroll to the first error field
         * because of our layout, the body is not scrollable
         */

        // select the first error field
        const [errorNode] = document.getElementsByClassName(
          ERROR_MESSAGE_CLASSNAME,
        );

        // manually scroll to it
        errorNode?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        showToast({
          message: locales.error,
          type: 'error',
        });
      },
    );
  const loading = quoteFormLoading || specialMentionsLoading;

  return (
    <FormProvider {...formMethods}>
      <QuoteFormContextProvider
        value={{
          clients,
          company,
          companySettings,
          isEditMode,
          metadata,
        }}
      >
        <FullPageLayout loading={loading}>
          <FormPageLayout.Root>
            <FormPageLayout.TopBar>
              <S.TopBarContainer>
                <Flex alignItems="center" gap="space-16">
                  <NavigationButton onClick={onClose} variant="close" />

                  <Text fontWeight="weight-500" size="lg" variant="primary">
                    {isEditMode ? locales.editTitle : locales.createTitle}
                  </Text>
                </Flex>

                <Stack alignItems="center" direction="row" spacing="space-32">
                  <Button
                    as="a"
                    href="https://help.shine.fr/fr/?q=facture"
                    minWidth="inherit"
                    onClick={() => logEvent({ name: 'Open Quotes FAQ' })}
                    rel="noopener noreferrer"
                    target="_blank"
                    variant="inline-secondary"
                  >
                    {locales.help}
                  </Button>

                  {isEditMode && onDelete ? (
                    <Button
                      isDisabled={confirmType === 'submit'}
                      onClick={onDelete}
                      variant="inline-secondary"
                    >
                      {locales.delete}
                    </Button>
                  ) : null}

                  <Button
                    isDisabled={confirmType === 'submit'}
                    isLoading={confirmType === 'save'}
                    onClick={handleConfirm('save')}
                    variant="inline-secondary"
                  >
                    {locales.save}
                  </Button>

                  <Button
                    icon="arrow-right"
                    isDisabled={confirmType === 'save'}
                    isLoading={confirmType === 'submit'}
                    onClick={handleConfirm('submit')}
                  >
                    {locales.ctaPrimary}
                  </Button>
                </Stack>
              </S.TopBarContainer>
            </FormPageLayout.TopBar>

            <FormPageLayout.Form>
              <QuoteForm />
            </FormPageLayout.Form>

            <FormPageLayout.Preview>
              <QuotePreview
                invoicingSpecialMentions={invoicingSpecialMentions}
              />
            </FormPageLayout.Preview>
          </FormPageLayout.Root>
        </FullPageLayout>
      </QuoteFormContextProvider>
    </FormProvider>
  );
};

export default FormLayout;
